import { Helmet } from 'react-helmet-async';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  language?: string;
  alternateUrls?: Record<string, string>;
}

// Supported EU languages + Ukraine + Switzerland + Norway (32 total)
const SUPPORTED_LANGUAGES = {
  // EU Official Languages (24)
  bg: 'Български', // Bulgarian
  cs: 'Čeština', // Czech
  da: 'Dansk', // Danish
  de: 'Deutsch', // German
  el: 'Ελληνικά', // Greek
  en: 'English', // English
  es: 'Español', // Spanish
  et: 'Eesti', // Estonian
  fi: 'Suomi', // Finnish
  fr: 'Français', // French
  ga: 'Gaeilge', // Irish
  hr: 'Hrvatski', // Croatian
  hu: 'Magyar', // Hungarian
  it: 'Italiano', // Italian
  lv: 'Latviešu', // Latvian
  lt: 'Lietuvių', // Lithuanian
  mt: 'Malti', // Maltese
  nl: 'Nederlands', // Dutch
  pl: 'Polski', // Polish
  pt: 'Português', // Portuguese
  ro: 'Română', // Romanian
  sk: 'Slovenčina', // Slovak
  sl: 'Slovenščina', // Slovenian
  sv: 'Svenska', // Swedish

  // Ukraine
  uk: 'Українська', // Ukrainian

  // Switzerland (Swiss-specific)
  rm: 'Rumantsch', // Romansh (Swiss)

  // Norway
  nb: 'Norsk bokmål', // Norwegian Bokmål
  nn: 'Nynorsk', // Norwegian Nynorsk
  se: 'Davvisámegiella', // Northern Sami
};

export function SEOHead({
  title = 'Olispices - Premium Spices & Exotic Blends | Authentic World Flavors',
  description = 'Discover premium quality spices, exotic blends, and authentic flavors from around the world. AI-powered personalized recommendations for your culinary adventures. Shop organic spices, traditional blends, and rare seasonings.',
  keywords = 'premium spices, exotic blends, organic spices, traditional seasonings, world flavors, culinary spices, spice store, authentic spices, rare seasonings, cooking ingredients',
  image = 'https://olispices.com/og-image.png',
  url = 'https://olispices.com/',
  type = 'website',
  language = 'en',
  alternateUrls = {},
}: SEOHeadProps) {
  return (
    <Helmet>
      {/* Dynamic language attribute */}
      <html lang={language} />

      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={url} />

      {/* Hreflang tags for multilingual SEO */}
      {Object.keys(alternateUrls).map(lang => (
        <link
          key={lang}
          rel="alternate"
          hrefLang={lang}
          href={alternateUrls[lang]}
        />
      ))}

      {/* Self-referencing hreflang for current language */}
      <link rel="alternate" hrefLang={language} href={url} />

      {/* x-default for international targeting */}
      <link
        rel="alternate"
        hrefLang="x-default"
        href={alternateUrls.en || url}
      />

      {/* Open Graph Tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Olispices" />
      <meta
        property="og:locale"
        content={
          language === 'en' ? 'en_US' : `${language}_${language.toUpperCase()}`
        }
      />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Additional SEO Tags */}
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />
      <meta name="author" content="Olispices" />
      <meta name="theme-color" content="#8B4513" />
    </Helmet>
  );
}
